<script setup lang="ts">
import { computed, ref } from 'vue';
import { EMPTY_KEYWORD_FEED_MESSAGE } from '@/constants';
import { DgrLoading } from '@stockmarkteam/donguri-ui';
import AdpDocumentCard from '@/components/common/adp-document-card.vue';
import BasicEmptyFeed from '@/components/common/basic-empty-feed.vue';
import { Article, ExecutionInformation, ThemeFeedType } from '@/types';
import { useAdpDocumentOptionMenu } from '@/utils/composables/useAdpDocumentOptionMenu';
import {
  INITIAL_VISIBLE_ARTICLE_COUNT,
  MULTI_EXECUTION_MAX_ARTICLE_COUNT,
} from '@/utils/personalNews';

type Props = {
  executionInfo: ExecutionInformation;
  articles?: Article[];
  previousTotalFeedCount: number;
  isLoaded: boolean;
  isFetchingData: boolean;
  themeFeedType: ThemeFeedType;
};
const props = withDefaults(defineProps<Props>(), {
  articles: () => [],
  removedArticles: () => [],
});

const executionTime = props.executionInfo.executionTime;

const visibleCount = ref(INITIAL_VISIBLE_ARTICLE_COUNT);

const visibleFeedArticles = computed(() =>
  props.articles.slice(0, visibleCount.value),
);

const optionMenu = useAdpDocumentOptionMenu({
  pageName: 'personal_news',
  feature: 'personal_feed',
  enablePersonalFeedSiteExclusion: true,
});
const personalFeedConfig = { notInterested: true, excludeMedia: true };

const isViewMoreButtonVisible = computed(
  () => props.articles.length > visibleCount.value,
);
</script>

<template>
  <div>
    <div class="execution-title c-title-bold--s">{{ executionTime }}更新</div>
    <div
      class="article-card"
      v-for="article in visibleFeedArticles"
      :key="`afternoon-${article.id}`"
    >
      <div class="divider"></div>
      <AdpDocumentCard
        width="100%"
        :adp-document="article"
        :feed-type="themeFeedType"
        :rank-in-whole-feed="previousTotalFeedCount + article.rank"
        :show-comment-count="1"
        mark-source="personal_feed"
        page-name="personal_news"
        feature="personal_feed"
        :disable-related-articles="false"
        :execution="executionInfo.execution"
        :option-actions="
          optionMenu.buildOptionActions(article, personalFeedConfig)
        "
        @option-action="
          name =>
            optionMenu.handleOptionAction(
              name,
              article,
              previousTotalFeedCount + article.rank,
            )
        "
      ></AdpDocumentCard>
    </div>
    <div class="footer">
      <template v-if="isViewMoreButtonVisible">
        <button
          class="view-more-button c-text-regular--m c-btn c-btn--auto c-btn--small c-btn--outline"
          @click="() => (visibleCount = MULTI_EXECUTION_MAX_ARTICLE_COUNT)"
        >
          すべて表示（{{ articles.length - visibleCount }}）
        </button>
        <div class="spacing-16" />
      </template>
      <DgrLoading
        v-if="!isLoaded || isFetchingData"
        class="loading loading-spacer"
      />
    </div>
    <template v-if="isLoaded && !isFetchingData && articles.length === 0">
      <div class="divider"></div>
      <BasicEmptyFeed>{{ EMPTY_KEYWORD_FEED_MESSAGE }}</BasicEmptyFeed>
    </template>
  </div>
</template>

<style scoped lang="scss">
.execution-title {
  padding: 8px 0;
}

.article-card {
  .document-card {
    border: 0;
    padding: 16px 0 12px 0;
  }
}

.divider {
  width: auto;
  border-bottom: 0.5px solid $color-gray400;
}

.footer {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.view-more-button {
  align-self: center;
}

.loading {
  align-self: center;
}
.loading-spacer {
  padding-bottom: 80vh;
}
</style>
